import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { MeDto } from '@scs/schemas';
import { Api, ApiError, setCsrf } from './lib/api';
import { navigate, useLocation } from './lib/router';
import { getLang, setLang, t } from './lib/i18n';
import { AcceptInvitePage, LoginPage, RegisterPage, ResetConfirmPage, ResetRequestPage } from './pages/Auth';
import { AdminPage } from './pages/Admin';
import { DashboardPage } from './pages/Dashboard';
import { EditorPage } from './editor/EditorPage';
import { PresetsPage } from './pages/Presets';
import { CatalogPage } from './pages/Catalog';

interface Session {
  me: MeDto;
  refresh(): Promise<void>;
  logout(): Promise<void>;
}

const SessionCtx = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionCtx);
  if (!s) throw new Error('No session');
  return s;
}

function useRerenderOnLang() {
  const [, set] = useState(0);
  useEffect(() => {
    const on = () => set((n) => n + 1);
    window.addEventListener('scs:lang', on);
    return () => window.removeEventListener('scs:lang', on);
  }, []);
}

export function App() {
  useRerenderOnLang();
  const { path } = useLocation();
  const [me, setMe] = useState<MeDto | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      const m = await Api.me();
      setCsrf(m.csrfToken);
      setMe(m);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setCsrf(null);
        setMe(null);
      } else throw e;
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => setMe(null));
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await Api.logout();
    } finally {
      setCsrf(null);
      setMe(null);
      navigate('/login');
    }
  }, []);

  if (path === '/reset') return <ResetConfirmPage />;
  if (path === '/forgot') return <ResetRequestPage />;
  if (path === '/invite') return <AcceptInvitePage onDone={refresh} />;
  if (me === undefined) return <div className="auth-page muted">…</div>;
  if (path === '/register' && me === null) return <RegisterPage onRegistered={(m) => { setCsrf(m.csrfToken); setMe(m); navigate('/', true); }} />;
  if (me === null) return <LoginPage onLogin={(m) => { setCsrf(m.csrfToken); setMe(m); if (path === '/login') navigate('/', true); }} />;

  const session: Session = { me, refresh, logout };
  const editorMatch = /^\/p\/([0-9a-f-]{36})$/.exec(path);
  return (
    <SessionCtx.Provider value={session}>
      {editorMatch ? <EditorPage key={editorMatch[1]} projectId={editorMatch[1]!} /> : path === '/presets' ? <PresetsPage /> : path === '/catalog' ? <CatalogPage /> : path === '/admin' ? <AdminPage /> : <DashboardPage />}
    </SessionCtx.Provider>
  );
}

export function TopbarUser() {
  const { me, logout, refresh } = useSession();
  const [pw, setPw] = useState(false);
  return (
    <>
      {me.workspaces.length > 1 && (
        <select
          className="input"
          style={{ height: 28 }}
          value={me.workspace.id}
          aria-label="Workspace"
          onChange={async (e) => {
            await Api.switchWorkspace(e.target.value);
            await refresh();
            navigate('/');
          }}
        >
          {me.workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name} ({w.role})
            </option>
          ))}
        </select>
      )}
      <button className="btn ghost sm" onClick={() => setLang(getLang() === 'sr' ? 'en' : 'sr')} title="Jezik / Language">
        {getLang() === 'sr' ? 'EN' : 'SR'}
      </button>
      <button
        className="btn ghost sm"
        title="Tema"
        onClick={() => {
          const next = document.documentElement.dataset.theme === 'light' ? 'dark' : 'light';
          document.documentElement.dataset.theme = next;
          try {
            localStorage.setItem('scs.theme', next);
          } catch {
            /* ignore */
          }
        }}
      >
        ◐
      </button>
      <button className="btn ghost sm" onClick={() => setPw(true)} title={t('auth.changePassword')}>
        {me.user.name}
      </button>
      <button className="btn sm" onClick={logout}>
        {t('auth.logout')}
      </button>
      {pw && <ChangePasswordModal onClose={() => setPw(false)} />}
    </>
  );
}

function ChangePasswordModal({ onClose }: { onClose(): void }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form
        className="card modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await Api.changePassword(cur, next);
            setMsg({ ok: true, text: t('auth.passwordChanged') });
            setCur('');
            setNext('');
          } catch (err) {
            setMsg({ ok: false, text: (err as Error).message });
          }
        }}
      >
        <h2>{t('auth.changePassword')}</h2>
        <label className="field">
          <span>{t('auth.currentPassword')}</span>
          <input className="input" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} required />
        </label>
        <label className="field">
          <span>{t('auth.newPassword')}</span>
          <input className="input" type="password" autoComplete="new-password" minLength={12} value={next} onChange={(e) => setNext(e.target.value)} required />
        </label>
        {msg && <p className={msg.ok ? 'ok-text' : 'error-text'}>{msg.text}</p>}
        <div className="actions">
          <button type="button" className="btn" onClick={onClose}>
            {t('dash.cancel')}
          </button>
          <button className="btn primary">{t('auth.changePassword')}</button>
        </div>
      </form>
    </div>
  );
}
